/**
 * SafeCurves header slots consumed by the shared shell via `safecurvesConfig.header`.
 *
 *   - `dashboard` — static `HeaderNavItem`.
 *   - `programs` — builder taking `(configData)`; null unless Programs is enabled (Settings is reached from Programs).
 *   - `playbook` — builder taking `(system)`; disabled unless Identify or
 *     Prioritize is present in `systemData`.
 *   - `method` — builder taking `(system)`; PDFs follow the active SafeCurves version series
 *     and which module layers are present in `systemData`.
 *
 * SafeSystem and About live in `#lib/apps/sharedHeader.ts`, not here.
 */

import type { ConfigData } from '#lib/apps/types.js';
import type { HeaderItem } from '#lib/components/Header.svelte';
import type { MenuItem } from '#lib/components/MenuButton.svelte';
import { asset } from '$app/paths';
import { gotoView } from '#lib/navigation.js';
import { versionsStore } from '#lib/stores/versionsStore.svelte.js';
import { SC_DATASET } from './layers';
import { programsEnabled } from './programs/gate';
import {
	EIdentify,
	EInterventions,
	EPrioritize,
	ESafeCurvesModule,
	type ISafeCurvesSystemLayers
} from './interfaces';

export const playbookLink = asset('methodology/SafeCurves_Playbook_v3.pdf');

export const dashboard: HeaderItem = {
	label: 'Dashboard',
	action: () => void gotoView('safecurves')
};

/** Programs nav item, gated on the `work_programs` flag and the active SafeCurves version (see `programsEnabled`). */
export function programs(configData: ConfigData): HeaderItem | null {
	if (!programsEnabled(configData)) return null;
	return { label: 'Programs', badge: 'Alpha', action: () => void gotoView('safecurves/programs') };
}

/**
 * Playbook link, disabled unless the loaded systemData includes SafeCurves
 * Identify or Prioritize — the modules the playbook covers.
 */
export function playbook(system: ISafeCurvesSystemLayers): HeaderItem {
	const identifyAvailable = system[ESafeCurvesModule.Identify]?.[EIdentify.Identify] != null;
	const prioritizeAvailable =
		system[ESafeCurvesModule.Prioritize]?.[EPrioritize.Prioritize] != null;
	return {
		label: 'Playbook',
		href: playbookLink,
		disabled: !identifyAvailable && !prioritizeAvailable
	};
}

// Methodology PDFs for the active version, each disabled when its module layer is absent.
export function method(system: ISafeCurvesSystemLayers): HeaderItem | null {
	if (Object.keys(system).length === 0)
		return {
			label: 'Method',
			href: '#'
		};

	const interventions = system[ESafeCurvesModule.Interventions];
	// Data series 2.1 ships the v2026.1 papers, which have no speed limit review.
	const series = versionsStore.version(SC_DATASET)?.series;
	const v2026 = series === '2.1';
	const [dir, v] = v2026
		? ['v2026_1', 'v2026.1']
		: series === '2.2'
			? ['v2.2', 'v2.2']
			: ['v2.3', 'v2.3'];
	const pdf = (label: string, paper: string, missing: boolean) => ({
		label: `${label} (*.pdf)`,
		href: `/methodology/${dir}/SafeCurves ${paper} White Paper_${v}.pdf`,
		type: 'download' as const,
		disabled: missing
	});
	const intervention = (label: string, paper: string, layer: EInterventions) =>
		pdf(label, `Interventions ${paper}`, interventions?.[layer] == null);

	const items: MenuItem[] = [
		{
			link: pdf(
				'SafeCurves Identify',
				'Identify',
				system[ESafeCurvesModule.Identify]?.[EIdentify.Identify] == null
			)
		},
		{
			link: pdf(
				'SafeCurves Prioritize',
				'Prioritize',
				system[ESafeCurvesModule.Prioritize]?.[EPrioritize.Prioritize] == null
			)
		},
		{
			submenu: {
				title: 'SafeCurves Interventions',
				links: [
					intervention(
						'Advisory signs and plaques',
						'Advisory Signs',
						EInterventions.CurveAdvisorySigns
					),
					intervention('No passing lines', 'No Passing Lines', EInterventions.PassingSightDistance),
					intervention(
						'Pavement friction focus areas',
						'Pavement Friction Focus Areas',
						EInterventions.PavementFrictionFocusAreas
					),
					...(v2026
						? []
						: [
								intervention(
									'Speed limit review',
									'Speed limit review',
									EInterventions.SpeedLimitReview
								)
							]),
					intervention(
						'Stopping Sight Distance',
						'Stopping Sight Distance',
						EInterventions.StoppingSightDistance
					)
				]
			}
		}
	];
	return { title: 'Method', items };
}
